/**
 * Calcula el índice inicial para la numeración de sitios basándose en los sitios existentes.
 * Extrae el número máximo del sufijo numérico de los nombres de sitios existentes
 * y retorna ese número + 1 para evitar duplicados.
 *
 * @param strataName - Nombre de la strata para filtrar sitios
 * @param existingSites - Array de sitios existentes (de DB y panel) para esa strata
 * @returns El índice inicial (número máximo + 1, o 1 si no hay sitios)
 */
export function getInitialSiteIndex(
  strataName: string,
  existingSites: Array<{ properties?: { name?: string; strata?: string } }>,
): number {
  if (!existingSites || existingSites.length === 0) {
    return 1
  }

  let maxNumber = 0

  // Filtrar sitios que pertenecen a esta strata y extraer números de sus nombres
  existingSites.forEach(site => {
    const siteStrata = site.properties?.strata
    const siteName = site.properties?.name

    // Solo considerar sitios de esta strata
    if (siteStrata !== strataName || !siteName) {
      return
    }

    // Extraer el número del final del nombre usando el mismo regex que sortSiteNamesNumerically
    const match = siteName.match(/(\d+)$/)
    if (match) {
      const number = parseInt(match[1], 10)
      if (!Number.isNaN(number) && number > maxNumber) {
        maxNumber = number
      }
    }
  })

  // Retornar el siguiente número disponible
  return maxNumber + 1
}

/**
 * Calcula el índice inicial para una NUEVA aleatorización completa, considerando
 * únicamente sitios ya persistidos (con id de backend). Los sitios aleatorios o
 * manuales aún no guardados se ignoran, de modo que re-aleatorizar un plan no
 * guardado siempre reinicia la numeración desde 1 en lugar de acumularla.
 *
 * @param strataName - Nombre de la strata para filtrar sitios
 * @param sites - Array de sitios de la strata (mezcla de guardados y sin guardar)
 * @returns El índice inicial basado solo en los sitios guardados
 */
export function getInitialSiteIndexFromSavedSites(
  strataName: string,
  sites: Array<{
    properties?: { name?: string; strata?: string; id?: string; newManualSite?: boolean }
  }>,
): number {
  const savedSites = (sites || []).filter(
    site => site.properties?.id && !site.properties?.newManualSite,
  )
  return getInitialSiteIndex(strataName, savedSites)
}
